import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';

import { cn } from '@/shared/lib/utils';
import { controlClass } from './input';

export interface SearchInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> {
  value: string;
  onValueChange: (value: string) => void;
}

// The one search field: lists, dialogs, ⌘K (DESIGN.md §5). No uppercasing —
// it matches, it doesn't store.
export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  ({ value, onValueChange, className, placeholder, ...props }, ref) => {
    const { t } = useTranslation();
    return (
      <div className={cn('relative w-full min-w-0', className)}>
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <input
          ref={ref}
          type="search"
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && value) {
              e.stopPropagation();
              onValueChange('');
            }
          }}
          placeholder={placeholder ?? t('common.search')}
          autoComplete="off"
          className={cn(controlClass, 'pl-9 pr-9 [&::-webkit-search-cancel-button]:hidden')}
          {...props}
        />
        {value ? (
          <button
            type="button"
            aria-label={t('common.clear')}
            onClick={() => onValueChange('')}
            className="absolute right-2 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        ) : null}
      </div>
    );
  },
);
SearchInput.displayName = 'SearchInput';
